import React, { useEffect, useState, useRef, useMemo } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import { DeleteModal } from "CommonComponent";
import isEqual from "lodash/isEqual";

const FULFILLMENT_SECTION_TITLE = "Fulfillment Letters and Postage";
const DEFAULT_PIECES_TITLE = "Cost of # Pieces ($)";
const DEFAULT_POSTAGE_TITLE = "Postage (at Cost)";

const CurrencyInput = ({
  value,
  onChange,
  isInvalid,
  className = "input-field",
  disabled = false,
  style,
  decimalScale = 2,
  prefix = "$ ",
  placeholder = "$ 0.00",
  allowNegative = false,
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => onChange?.({ target: { value: values.value } })}
    thousandSeparator
    decimalScale={decimalScale}
    allowNegative={allowNegative}
    prefix={prefix}
    placeholder={placeholder}
    customInput={Form.Control}
    isInvalid={isInvalid}
    className={className}
    disabled={disabled}
    style={style}
  />
);

const toMonthKey = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}`;
};

const isMonthKey = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}$/.test(value);

const resolveInvoiceMonthKey = (invoiceMonthKey = "") => {
  if (isMonthKey(invoiceMonthKey)) {
    return invoiceMonthKey;
  }

  return toMonthKey(new Date());
};

const buildInvoiceYearMonthRows = (invoiceMonthKey = "") => {
  const resolvedMonthKey = resolveInvoiceMonthKey(invoiceMonthKey);
  const year = Number(String(resolvedMonthKey).slice(0, 4));
  const rows = [];

  for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
    const date = new Date(year, monthIndex, 1);
    rows.push({
      key: toMonthKey(date),
      label: date.toLocaleString("en-US", { month: "short", year: "2-digit" }),
    });
  }

  return rows;
};

const toAlwaysNegativeAmount = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return "";
  const parsed = Number(String(value).replace(/[$,%\s,]/g, ""));
  if (!Number.isFinite(parsed)) return "";
  if (parsed === 0) return "0";
  return String(-Math.abs(parsed));
};

const normalizeMonthlyMap = (monthlyMap, allowedFields = []) => {
  if (!monthlyMap || typeof monthlyMap !== "object" || Array.isArray(monthlyMap)) {
    return {};
  }

  const normalized = {};

  Object.entries(monthlyMap).forEach(([monthKey, monthValues]) => {
    if (!/^\d{4}-\d{2}$/.test(monthKey)) {
      return;
    }

    const source =
      monthValues && typeof monthValues === "object" && !Array.isArray(monthValues)
        ? monthValues
        : {};

    const nextValues = {};
    allowedFields.forEach((field) => {
      const fieldValue = source?.[field] ?? "";
      nextValues[field] =
        field === "voidAmount" ? toAlwaysNegativeAmount(fieldValue) : fieldValue;
    });

    normalized[monthKey] = nextValues;
  });

  return normalized;
};

const parseNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeWholeNonNegativeValue = (value) => {
  if (value === undefined || value === null) return "";

  const raw = String(value).trim();
  if (!raw) return "";

  if (!/^(\d+)(\.\d+)?$/.test(raw)) {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
    return null;
  }

  return String(parsed);
};

const isTruthy = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const normalizePerPieceRateByBrand = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const normalized = {};
  Object.entries(value).forEach(([brand, rate]) => {
    const brandName = toNormalizedBrandName(brand);
    if (!brandName) return;
    normalized[brandName] = rate;
  });

  return normalized;
};

const normalizeStringMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const normalized = {};
  Object.entries(value).forEach(([brand, text]) => {
    const brandName = toNormalizedBrandName(brand);
    if (!brandName) return;
    normalized[brandName] = text === undefined || text === null ? "" : String(text);
  });

  return normalized;
};

const toSafeMap = (value, normalizer) => normalizer(value && typeof value === "object" ? value : {});

const formatCurrency = (value) => {
  const numeric = parseNumber(value);
  if (numeric === 0) {
    return "-";
  }

  const formatted = `$${Number(Math.abs(numeric).toFixed(2)).toLocaleString()}`;
  return numeric < 0 ? `-${formatted}` : formatted;
};

const formatCurrencyWithZero = (value) => {
  const numeric = parseNumber(value);
  return `$${Number(numeric.toFixed(2)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const toNormalizedBrandName = (value) => String(value || "").trim();

const hasBrandMapValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "number") return Number.isFinite(value);
  return String(value).trim() !== "";
};

const seedBrandMap = (existingMap, brands = [], fallbackValue = "") => {
  const next =
    existingMap && typeof existingMap === "object" && !Array.isArray(existingMap)
      ? { ...existingMap }
      : {};

  brands.forEach((brand) => {
    const brandName = toNormalizedBrandName(brand);
    if (!brandName) return;
    if (!hasBrandMapValue(next[brandName])) {
      next[brandName] = fallbackValue ?? "";
    }
  });

  return next;
};

const seedBrandwiseMaps = (
  config = {},
  brands = [],
  { piecesTitleFallback = DEFAULT_PIECES_TITLE, postageTitleFallback = DEFAULT_POSTAGE_TITLE } = {}
) => ({
  pieceTitleByBrand: seedBrandMap(
    config?.pieceTitleByBrand,
    brands,
    config?.title || piecesTitleFallback
  ),
  postageTitleByBrand: seedBrandMap(
    config?.postageTitleByBrand,
    brands,
    config?.postageTitle || postageTitleFallback
  ),
  perPieceRateByBrand: seedBrandMap(
    config?.perPieceRateByBrand,
    brands,
    config?.perPieceRate ?? ""
  ),
});

const toDecimalString = (value) => {
  if (value === undefined || value === null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const normalized = raw.replace(/[$,%\s,]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? String(parsed) : "";
};

const extractProgramBrands = (fulfillmentSection = {}, programBrands = []) => {
  const section =
    fulfillmentSection && typeof fulfillmentSection === "object"
      ? fulfillmentSection
      : {};

  const sectionBrands = [
    ...(Array.isArray(section?.program_brands) ? section.program_brands : []),
    ...(Array.isArray(section?.brand_names) ? section.brand_names : []),
    ...(Array.isArray(section?.brands) ? section.brands : []),
  ];

  const collected = [...programBrands, ...sectionBrands]
    .map(toNormalizedBrandName)
    .filter(Boolean);

  const unique = [];
  const seen = new Set();

  collected.forEach((brand) => {
    const key = brand.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(brand);
  });

  return unique;
};

const normalizePostageRows = (postageRows = [], fulfillmentSection = {}) => {
  const fromProps = Array.isArray(postageRows) ? postageRows : [];

  const fromSection =
    Array.isArray(fulfillmentSection?.postage_rows)
      ? fulfillmentSection.postage_rows
      : Array.isArray(fulfillmentSection?.postage_data)
        ? fulfillmentSection.postage_data
        : [];

  const sourceRows = fromProps.length > 0 ? fromProps : fromSection;

  return sourceRows
    .map((row, index) => {
      const rowObj = row && typeof row === "object" ? row : {};
      const brandId = String(rowObj?.brand_id || rowObj?.brandId || "").trim();
      const brandName =
        toNormalizedBrandName(
          rowObj?.brand_name ||
            rowObj?.brandName ||
            rowObj?.brand ||
            rowObj?.program_brand ||
            rowObj?.programBrand
        ) || "";

      const rowKey = brandId || `${brandName || "row"}-${index}`;

      return {
        rowKey,
        brandId,
        brandName,
        items: toDecimalString(rowObj?.items),
        postageUsed: toDecimalString(rowObj?.postage_used ?? rowObj?.postageUsed),
      };
    })
    .filter((row) => row.brandId || row.brandName);
};

const sanitizeFulfillmentSection = (section = {}) => {
  const safeSection =
    section && typeof section === "object" && !Array.isArray(section) ? section : {};

  const { program_brands, postage_rows, postage_data, ...fields } = safeSection;
  return fields;
};

const hasEditableValue = (value) => {
  if (value === undefined || value === null) return false;
  return String(value).trim() !== "";
};

const createFulfillmentMiscRow = (id, data = {}, defaultItems = "") => {
  const wholeItems = hasEditableValue(data.items)
    ? sanitizeWholeNonNegativeValue(data.items)
    : null;

  return {
    id,
    title: data.title === undefined || data.title === null ? "" : String(data.title),
    rate: data.rate === undefined || data.rate === null ? "" : data.rate,
    items: wholeItems !== null && wholeItems !== "" ? wholeItems : defaultItems,
  };
};

const toTwoDecimalString = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return "";
  const parsed = Number(String(value).replace(/[$,%\s,]/g, ""));
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "";
};

const normalizeFulfillmentMiscRows = (value, defaultItems = "") => {
  const source = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? [value]
      : [];

  return source
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createFulfillmentMiscRow(row?.id ?? index + 1, row, defaultItems));
};

const toRowArray = (value) =>
  Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? [value]
      : [];

const createTitleChargeRow = (id, data = {}, defaultTitle = "") => ({
  id,
  title:
    data.title === undefined || data.title === null || String(data.title).trim() === ""
      ? defaultTitle
      : String(data.title),
  charge:
    data.charge !== undefined && data.charge !== null && String(data.charge).trim() !== ""
      ? data.charge
      : data.miscellaneousSuppliesReceipts !== undefined &&
          data.miscellaneousSuppliesReceipts !== null
        ? data.miscellaneousSuppliesReceipts
        : "",
});

const createGiftCardInvoiceRow = (id, data = {}) => ({
  id,
  title:
    data.title === undefined || data.title === null || String(data.title).trim() === ""
      ? "Gift Cards"
      : String(data.title),
  processingFee: data.processingFee === undefined || data.processingFee === null ? "" : data.processingFee,
  numberProcessed:
    data.numberProcessed !== undefined && data.numberProcessed !== null && String(data.numberProcessed).trim() !== ""
      ? data.numberProcessed
      : data.numberOfGiftCardsOrdered !== undefined && data.numberOfGiftCardsOrdered !== null
        ? data.numberOfGiftCardsOrdered
        : "",
});

const createFedexInvoiceRow = (id, data = {}) => ({
  id,
  title:
    data.title === undefined || data.title === null || String(data.title).trim() === ""
      ? "Shipment Charges"
      : String(data.title),
  baseCharge: data.baseCharge === undefined || data.baseCharge === null ? "" : data.baseCharge,
});

const normalizeTitleChargeRows = (value, defaultTitle) =>
  toRowArray(value)
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createTitleChargeRow(row?.id ?? index + 1, row, defaultTitle));

const normalizeGiftCardInvoiceRows = (value) =>
  toRowArray(value)
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createGiftCardInvoiceRow(row?.id ?? index + 1, row));

const normalizeFedexInvoiceRows = (value) =>
  toRowArray(value)
    .filter((row) => row && typeof row === "object")
    .map((row, index) => createFedexInvoiceRow(row?.id ?? index + 1, row));

const toTitleChargeInvoicePayload = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const next = {
      title: String(row?.title || "").trim(),
    };
    const charge = toTwoDecimalString(row?.charge);
    if (charge !== "") {
      next.charge = charge;
    }
    return next;
  });

const toGiftCardsInvoicePayload = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const next = {
      title: String(row?.title || "").trim(),
      processingFee: toTwoDecimalString(row?.processingFee),
    };
    const numberProcessed = sanitizeWholeNonNegativeValue(row?.numberProcessed);
    if (numberProcessed) {
      next.numberProcessed = Number(numberProcessed);
    }
    return next;
  });

const toFedexInvoicePayload = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    title: String(row?.title || "").trim(),
    baseCharge: toTwoDecimalString(row?.baseCharge),
  }));

const toFulfillmentMiscInvoicePayload = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const next = {
        title: String(row?.title || "").trim(),
        rate: toTwoDecimalString(row?.rate),
      };
      const items = sanitizeWholeNonNegativeValue(row?.items);
      if (items) {
        next.items = items;
      }
      return next;
    })
    .filter((row) => row.title || row.rate || row.items);

const FulfilmentTab = ({
  selectedSections = [],
  onSectionDataChange,
  initialSectionData = {},
  getFieldError = () => "",
  invoiceMonthKey = "",
  postageRows = [],
  programBrands = [],
}) => {
  const shouldShow = (sectionId) => selectedSections.includes(sectionId);
  const hasSelectedSection = selectedSections.length > 0;
  const monthRows = useMemo(() => buildInvoiceYearMonthRows(invoiceMonthKey), [invoiceMonthKey]);
  const currentMonthKey = useMemo(
    () => resolveInvoiceMonthKey(invoiceMonthKey),
    [invoiceMonthKey]
  );

  const buildFulfillmentConfig = (source = {}) => ({
    title: "Fulfillment",
    postageTitle: "Postage (at Cost)",
    perPieceRate: "",
    chargeBrandwise: false,
    perPieceRateByBrand: {},
    pieceTitleByBrand: {},
    postageTitleByBrand: {},
    ...((source?.fulfillment && typeof source.fulfillment === "object"
      ? {
          title: source.fulfillment.title || "Fulfillment",
          postageTitle: source.fulfillment.postageTitle || "Postage (at Cost)",
          perPieceRate: source.fulfillment.perPieceRate || "",
          chargeBrandwise: isTruthy(source.fulfillment.chargeBrandwise),
          perPieceRateByBrand: toSafeMap(
            source.fulfillment.perPieceRateByBrand,
            normalizePerPieceRateByBrand
          ),
          pieceTitleByBrand: toSafeMap(
            source.fulfillment.pieceTitleByBrand,
            normalizeStringMap
          ),
          postageTitleByBrand: toSafeMap(
            source.fulfillment.postageTitleByBrand,
            normalizeStringMap
          ),
        }
      : {})),
  });

  const buildTrackerConfig = (source = {}) => ({
    checkProcessingTitle: "#of Checks",
    refundChecksIssuedTitle: "Check Amount ($)",
    checkProcessingRate: "",
    voidProcessingTitle: "#of Voided",
    voidProcessingRate: "",
    stoppedPaymentTitle: "Stop Pay.",
    stoppedPaymentRate: "",
    monthly: normalizeMonthlyMap(source?.fulfillment_tracker?.monthly, [
      "checkCount",
      "checkAmount",
      "voidCount",
      "voidAmount",
      "stoppedPaymentAmount",
    ]),
    ...((source?.fulfillment_tracker && typeof source.fulfillment_tracker === "object"
      ? (() => {
          const trackerFields = { ...source.fulfillment_tracker };
          delete trackerFields.title;
          delete trackerFields.monthly;
          return trackerFields;
        })()
      : {})),
  });

  const buildTracker1Config = (source = {}) => ({
    retrievalTitle: "Retrieval",
    retrievalRate:
      source?.fulfillment_tracker_1?.retrievalRate ??
      source?.fulfillment_tracker_1?.retrievalHoursRate ??
      "",
    nonRetrievalTitle: "Non-Retrieval",
    nonRetrievalRate: "",
    billRetrievalByHours: isTruthy(
      source?.fulfillment_tracker_1?.billRetrievalByHours ?? true
    ),
    minutesPerRetrieval: "",
    monthly: normalizeMonthlyMap(source?.fulfillment_tracker_1?.monthly, [
      "retrievalCount",
      "nonRetrievalCount",
    ]),
    ...((source?.fulfillment_tracker_1 && typeof source.fulfillment_tracker_1 === "object"
      ? (() => {
          const trackerFields = { ...source.fulfillment_tracker_1 };
          delete trackerFields.minutesPerRetrievalTitle;
          delete trackerFields.monthly;
          return trackerFields;
        })()
      : {})),
  });

  const [suppliesRows, setSuppliesRows] = useState(() =>
    normalizeTitleChargeRows(initialSectionData?.supplies, "Supplies")
  );
  const nextSuppliesIdRef = useRef(
    normalizeTitleChargeRows(initialSectionData?.supplies, "Supplies").length + 1
  );

  const [storageRows, setStorageRows] = useState(() =>
    normalizeTitleChargeRows(initialSectionData?.storage, "Storage")
  );
  const nextStorageIdRef = useRef(
    normalizeTitleChargeRows(initialSectionData?.storage, "Storage").length + 1
  );

  const [giftCardRows, setGiftCardRows] = useState(() =>
    normalizeGiftCardInvoiceRows(initialSectionData?.gift_cards)
  );
  const nextGiftCardIdRef = useRef(
    normalizeGiftCardInvoiceRows(initialSectionData?.gift_cards).length + 1
  );

  const [fedexRows, setFedexRows] = useState(() =>
    normalizeFedexInvoiceRows(initialSectionData?.fedex_expenses)
  );
  const nextFedexIdRef = useRef(
    normalizeFedexInvoiceRows(initialSectionData?.fedex_expenses).length + 1
  );

  const [fulfillmentConfig, setFulfillmentConfig] = useState({
    ...buildFulfillmentConfig(initialSectionData),
  });

  const [trackerConfig, setTrackerConfig] = useState({
    ...buildTrackerConfig(initialSectionData),
  });

  const [tracker1Config, setTracker1Config] = useState({
    ...buildTracker1Config(initialSectionData),
  });

  const [postageRowsState, setPostageRowsState] = useState(() =>
    normalizePostageRows(postageRows, initialSectionData?.fulfillment)
  );
  const [fulfillmentMiscRows, setFulfillmentMiscRows] = useState(() =>
    normalizeFulfillmentMiscRows(initialSectionData?.fulfillment_misc)
  );
  const nextFulfillmentMiscIdRef = useRef(
    normalizeFulfillmentMiscRows(initialSectionData?.fulfillment_misc).length + 1
  );

  const hydratedSnapshotRef = useRef({
    fulfillment: buildFulfillmentConfig(initialSectionData),
    tracker: buildTrackerConfig(initialSectionData),
    tracker1: buildTracker1Config(initialSectionData),
    supplies: normalizeTitleChargeRows(initialSectionData?.supplies, "Supplies"),
    storage: normalizeTitleChargeRows(initialSectionData?.storage, "Storage"),
    giftCards: normalizeGiftCardInvoiceRows(initialSectionData?.gift_cards),
    fedex: normalizeFedexInvoiceRows(initialSectionData?.fedex_expenses),
    fulfillmentMisc: normalizeFulfillmentMiscRows(initialSectionData?.fulfillment_misc),
  });
  const isHydratingRef = useRef(false);
  const lastEmittedPayloadRef = useRef(null);

  useEffect(() => {
    const nextFulfillmentConfig = buildFulfillmentConfig({
      ...initialSectionData,
      fulfillment: sanitizeFulfillmentSection(initialSectionData?.fulfillment),
    });
    const nextTrackerConfig = buildTrackerConfig(initialSectionData);
    const nextTracker1Config = buildTracker1Config(initialSectionData);
    const nextSuppliesRows = normalizeTitleChargeRows(initialSectionData?.supplies, "Supplies");
    const nextStorageRows = normalizeTitleChargeRows(initialSectionData?.storage, "Storage");
    const nextGiftCardRows = normalizeGiftCardInvoiceRows(initialSectionData?.gift_cards);
    const nextFedexRows = normalizeFedexInvoiceRows(initialSectionData?.fedex_expenses);
    const nextPostageRows = normalizePostageRows(postageRows, initialSectionData?.fulfillment);
    const postageItemsTotal = nextPostageRows.reduce(
      (sum, row) => sum + parseNumber(row?.items),
      0
    );
    const nextFulfillmentMiscRows = normalizeFulfillmentMiscRows(
      initialSectionData?.fulfillment_misc,
      postageItemsTotal > 0 ? String(postageItemsTotal) : ""
    );

    const nextSnapshot = {
      fulfillment: nextFulfillmentConfig,
      tracker: nextTrackerConfig,
      tracker1: nextTracker1Config,
      supplies: nextSuppliesRows,
      storage: nextStorageRows,
      giftCards: nextGiftCardRows,
      fedex: nextFedexRows,
      postageRows: nextPostageRows,
      fulfillmentMisc: nextFulfillmentMiscRows,
    };

    if (isEqual(hydratedSnapshotRef.current, nextSnapshot)) {
      return;
    }

    isHydratingRef.current = true;

    setFulfillmentConfig((prev) =>
      isEqual(prev, nextFulfillmentConfig) ? prev : nextFulfillmentConfig
    );
    setTrackerConfig((prev) =>
      isEqual(prev, nextTrackerConfig) ? prev : nextTrackerConfig
    );
    setTracker1Config((prev) =>
      isEqual(prev, nextTracker1Config) ? prev : nextTracker1Config
    );
    setSuppliesRows((prev) => (isEqual(prev, nextSuppliesRows) ? prev : nextSuppliesRows));
    setStorageRows((prev) => (isEqual(prev, nextStorageRows) ? prev : nextStorageRows));
    setGiftCardRows((prev) => (isEqual(prev, nextGiftCardRows) ? prev : nextGiftCardRows));
    setFedexRows((prev) => (isEqual(prev, nextFedexRows) ? prev : nextFedexRows));
    setPostageRowsState((prev) =>
      isEqual(prev, nextPostageRows) ? prev : nextPostageRows
    );
    setFulfillmentMiscRows((prev) =>
      isEqual(prev, nextFulfillmentMiscRows) ? prev : nextFulfillmentMiscRows
    );
    nextFulfillmentMiscIdRef.current = nextFulfillmentMiscRows.length + 1;
    nextSuppliesIdRef.current = nextSuppliesRows.length + 1;
    nextStorageIdRef.current = nextStorageRows.length + 1;
    nextGiftCardIdRef.current = nextGiftCardRows.length + 1;
    nextFedexIdRef.current = nextFedexRows.length + 1;

    hydratedSnapshotRef.current = nextSnapshot;
  }, [initialSectionData]);

  const onSectionDataChangeRef = useRef(onSectionDataChange);
  useEffect(() => {
    onSectionDataChangeRef.current = onSectionDataChange;
  }, [onSectionDataChange]);

  const handleChange = (setter, field) => (event) => {
    const nextValue = event?.target?.value;

    setter((prev) => {
      if (String(prev?.[field] ?? "") === String(nextValue ?? "")) {
        return prev;
      }

      return {
        ...prev,
        [field]: nextValue,
      };
    });
  };

  const handleMonthlyFieldChange = (
    setter,
    monthKey,
    field,
    { wholeNonNegative = false, alwaysNegative = false } = {}
  ) => (event) => {
    const inputValue = event?.target?.value;
    const value = wholeNonNegative
      ? sanitizeWholeNonNegativeValue(inputValue)
      : alwaysNegative
        ? toAlwaysNegativeAmount(inputValue)
        : inputValue;

    if (wholeNonNegative && value === null) {
      return;
    }

    setter((prev) => {
      const previousMonthly = prev?.monthly || {};
      const previousMonthValues = previousMonthly?.[monthKey] || {};

      if (String(previousMonthValues?.[field] ?? "") === String(value ?? "")) {
        return prev;
      }

      return {
        ...prev,
        monthly: {
          ...previousMonthly,
          [monthKey]: {
            ...previousMonthValues,
            [field]: value,
          },
        },
      };
    });
  };

  const trackerTotals = useMemo(() => {
    return monthRows.reduce(
      (acc, row) => {
        const monthly = trackerConfig?.monthly?.[row.key] || {};

        return {
          checks: acc.checks + parseNumber(monthly.checkCount),
          checkAmount: acc.checkAmount + parseNumber(monthly.checkAmount),
          voided: acc.voided + parseNumber(monthly.voidCount),
          voidAmount: acc.voidAmount + parseNumber(monthly.voidAmount),
          stoppedAmount: acc.stoppedAmount + parseNumber(monthly.stoppedPaymentAmount),
        };
      },
      {
        checks: 0,
        checkAmount: 0,
        voided: 0,
        voidAmount: 0,
        stoppedAmount: 0,
      }
    );
  }, [monthRows, trackerConfig?.monthly]);

  const tracker1Totals = useMemo(() => {
    return monthRows.reduce(
      (acc, row) => {
        const monthly = tracker1Config?.monthly?.[row.key] || {};

        return {
          retrievalCount: acc.retrievalCount + parseNumber(monthly.retrievalCount),
          nonRetrievalCount:
            acc.nonRetrievalCount + parseNumber(monthly.nonRetrievalCount),
        };
      },
      {
        retrievalCount: 0,
        nonRetrievalCount: 0,
      }
    );
  }, [monthRows, tracker1Config?.monthly]);

  const currentTrackerMonthValues = trackerConfig?.monthly?.[currentMonthKey] || {};
  const currentTracker1MonthValues = tracker1Config?.monthly?.[currentMonthKey] || {};
  const isBillRetrievalByHours = isTruthy(tracker1Config?.billRetrievalByHours);
  const retrievalCountCurrentMonth = parseNumber(currentTracker1MonthValues?.retrievalCount);
  const minutesPerRetrieval = parseNumber(tracker1Config?.minutesPerRetrieval);
  const monthlyHours = (retrievalCountCurrentMonth * minutesPerRetrieval) / 60;

  const fulfillmentBrands = useMemo(
    () =>
      extractProgramBrands(fulfillmentConfig, [
        ...programBrands,
        ...postageRowsState.map((row) => row.brandName),
      ]),
    [fulfillmentConfig, programBrands, postageRowsState]
  );

  const postageItemsTotal = useMemo(() => {
    const total = postageRowsState.reduce((sum, row) => sum + parseNumber(row?.items), 0);
    return Number.isFinite(total) && total > 0 ? Math.round(total) : 0;
  }, [postageRowsState]);

  useEffect(() => {
    if (!(postageItemsTotal > 0)) return;
    const defaultItems = String(postageItemsTotal);

    setFulfillmentMiscRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (hasEditableValue(row?.items)) return row;
        changed = true;
        return { ...row, items: defaultItems };
      });
      return changed ? next : prev;
    });
  }, [postageItemsTotal]);

  useEffect(() => {
    if (!Boolean(fulfillmentConfig?.chargeBrandwise) || fulfillmentBrands.length === 0) {
      return;
    }

    setFulfillmentConfig((prev) => {
      const seeded = seedBrandwiseMaps(prev, fulfillmentBrands);
      if (
        isEqual(prev?.pieceTitleByBrand, seeded.pieceTitleByBrand) &&
        isEqual(prev?.postageTitleByBrand, seeded.postageTitleByBrand) &&
        isEqual(prev?.perPieceRateByBrand, seeded.perPieceRateByBrand)
      ) {
        return prev;
      }

      return {
        ...prev,
        ...seeded,
      };
    });
  }, [fulfillmentConfig?.chargeBrandwise, fulfillmentBrands]);

  const serializedPostageRows = useMemo(
    () =>
      postageRowsState
        .map((row) => ({
          ...(row?.brandId ? { brand_id: row.brandId } : {}),
          ...(row?.brandName ? { brand_name: row.brandName } : {}),
          items: toDecimalString(row?.items),
          postage_used: toDecimalString(row?.postageUsed),
        }))
        .filter((row) => Boolean(row?.brand_id || row?.brand_name)),
    [postageRowsState]
  );

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }

    const isChargeBrandwise = Boolean(fulfillmentConfig?.chargeBrandwise);
    const seededBrandMaps = isChargeBrandwise
      ? seedBrandwiseMaps(
          {
            ...fulfillmentConfig,
            perPieceRateByBrand: toSafeMap(
              fulfillmentConfig?.perPieceRateByBrand,
              normalizePerPieceRateByBrand
            ),
            pieceTitleByBrand: toSafeMap(
              fulfillmentConfig?.pieceTitleByBrand,
              normalizeStringMap
            ),
            postageTitleByBrand: toSafeMap(
              fulfillmentConfig?.postageTitleByBrand,
              normalizeStringMap
            ),
          },
          fulfillmentBrands
        )
      : {
          perPieceRateByBrand: toSafeMap(
            fulfillmentConfig?.perPieceRateByBrand,
            normalizePerPieceRateByBrand
          ),
          pieceTitleByBrand: toSafeMap(
            fulfillmentConfig?.pieceTitleByBrand,
            normalizeStringMap
          ),
          postageTitleByBrand: toSafeMap(
            fulfillmentConfig?.postageTitleByBrand,
            normalizeStringMap
          ),
        };

    const fulfillmentPayload = {
      title: fulfillmentConfig?.title || "Fulfillment",
      postageTitle: fulfillmentConfig?.postageTitle || DEFAULT_POSTAGE_TITLE,
      perPieceRate: fulfillmentConfig?.perPieceRate || "",
      chargeBrandwise: isChargeBrandwise,
      perPieceRateByBrand: seededBrandMaps.perPieceRateByBrand,
      pieceTitleByBrand: seededBrandMaps.pieceTitleByBrand,
      postageTitleByBrand: seededBrandMaps.postageTitleByBrand,
      program_brands: fulfillmentBrands,
      postage_rows: serializedPostageRows,
    };

    const trackerPayload = {
      checkProcessingTitle: trackerConfig?.checkProcessingTitle || "",
      refundChecksIssuedTitle: trackerConfig?.refundChecksIssuedTitle || "",
      checkProcessingRate: trackerConfig?.checkProcessingRate || "",
      voidProcessingTitle: trackerConfig?.voidProcessingTitle || "",
      voidProcessingRate: trackerConfig?.voidProcessingRate || "",
      stoppedPaymentTitle: trackerConfig?.stoppedPaymentTitle || "",
      stoppedPaymentRate: trackerConfig?.stoppedPaymentRate || "",
      monthly: Object.entries(trackerConfig?.monthly || {}).reduce((acc, [monthKey, monthValues]) => {
        const source = monthValues && typeof monthValues === "object" ? monthValues : {};
        acc[monthKey] = {
          ...source,
          voidAmount: toAlwaysNegativeAmount(source.voidAmount),
        };
        return acc;
      }, {}),
    };

    const tracker1Payload = {
      retrievalTitle: tracker1Config?.retrievalTitle || "",
      retrievalRate: tracker1Config?.retrievalRate || "",
      retrievalHoursRate: tracker1Config?.retrievalRate || "",
      nonRetrievalTitle: tracker1Config?.nonRetrievalTitle || "",
      nonRetrievalRate: tracker1Config?.nonRetrievalRate || "",
      billRetrievalByHours: Boolean(tracker1Config?.billRetrievalByHours),
      ...(isTruthy(tracker1Config?.billRetrievalByHours)
        ? { minutesPerRetrieval: tracker1Config?.minutesPerRetrieval || "" }
        : {}),
      monthly: tracker1Config?.monthly || {},
    };

    const payload = {
      fulfillment: fulfillmentPayload,
      fulfillment_tracker: trackerPayload,
      fulfillment_tracker_1: tracker1Payload,
      supplies: toTitleChargeInvoicePayload(suppliesRows),
      storage: toTitleChargeInvoicePayload(storageRows),
      gift_cards: toGiftCardsInvoicePayload(giftCardRows),
      fedex_expenses: toFedexInvoicePayload(fedexRows),
      fulfillment_misc: toFulfillmentMiscInvoicePayload(fulfillmentMiscRows),
    };

    if (isEqual(lastEmittedPayloadRef.current, payload)) {
      return;
    }

    lastEmittedPayloadRef.current = payload;
    onSectionDataChangeRef.current?.(payload);
  }, [
    fulfillmentConfig,
    fulfillmentBrands,
    serializedPostageRows,
    postageRowsState,
    trackerConfig,
    tracker1Config,
    suppliesRows,
    storageRows,
    giftCardRows,
    fedexRows,
    fulfillmentMiscRows,
  ]);

  const getError = (sectionId, field, rowIndex = null) =>
    getFieldError?.(sectionId, field, rowIndex) || "";
  const getBrandMapError = (field, brand) => {
    const brandName = toNormalizedBrandName(brand);
    if (!brandName) return "";
    return (
      getError("fulfillment", `${field}.${brandName}`) ||
      getError("fulfillment", `${field}.${brand}`) ||
      getError("fulfillment", field)
    );
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleRowFieldChange = (setter, rowId, field, { wholeNonNegative = false } = {}) => (event) => {
    let nextValue = event?.target?.value;
    if (wholeNonNegative) {
      nextValue = sanitizeWholeNonNegativeValue(nextValue);
      if (nextValue === null) {
        return;
      }
    }
    setter((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: nextValue } : row))
    );
  };

  const handleFulfillmentMiscFieldChange = (rowId, field) => (event) => {
    let nextValue = event?.target?.value;
    if (field === "items") {
      nextValue = sanitizeWholeNonNegativeValue(nextValue);
      if (nextValue === null) {
        return;
      }
    }
    setFulfillmentMiscRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: nextValue } : row))
    );
  };

  const handleAddFulfillmentMiscRow = () => {
    const nextId = nextFulfillmentMiscIdRef.current;
    nextFulfillmentMiscIdRef.current += 1;
    const defaultItems = postageItemsTotal > 0 ? String(postageItemsTotal) : "";
    setFulfillmentMiscRows((prev) => [
      ...prev,
      createFulfillmentMiscRow(nextId, {}, defaultItems),
    ]);
  };

  const handleAddRow = (setter, idRef, createRow, defaultTitle) => () => {
    const nextId = idRef.current;
    idRef.current += 1;
    setter((prev) => [...prev, createRow(nextId, {}, defaultTitle)]);
  };

  const handleDeleteRowClick = (sectionId, rowId) => {
    setPendingDelete({ sectionId, rowId });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setPendingDelete(null);
  };

  const handleConfirmDeleteRow = () => {
    const sectionId = pendingDelete?.sectionId;
    const rowId = pendingDelete?.rowId;
    const setters = {
      fulfillment_misc: setFulfillmentMiscRows,
      supplies: setSuppliesRows,
      storage: setStorageRows,
      gift_cards: setGiftCardRows,
      fedex_expenses: setFedexRows,
    };
    setters[sectionId]?.((prev) => prev.filter((row) => row.id !== rowId));
    handleCloseDeleteModal();
  };

  return (
    <>
      {!hasSelectedSection && (
        <div className="invoice-section-div mb-0 text-center">
          <span className="font-16 font-weight-bold heading-color">
            First you have to select at least one section.
          </span>
        </div>
      )}

      {shouldShow("fulfillment") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2 mb-3">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">
              {FULFILLMENT_SECTION_TITLE}
            </span>
          </h3>

          <Row className="mb-3">
            <Col sm={12}>
              <Form.Group className="mb-0">
                <Form.Check
                  type="switch"
                  label="Charge Brandwise"
                  checked={Boolean(fulfillmentConfig?.chargeBrandwise)}
                  onChange={(event) => {
                    const checked = Boolean(event?.target?.checked);
                    setFulfillmentConfig((prev) => {
                      if (!checked) {
                        return {
                          ...prev,
                          chargeBrandwise: false,
                        };
                      }

                      return {
                        ...prev,
                        chargeBrandwise: true,
                        ...seedBrandwiseMaps(prev, fulfillmentBrands),
                      };
                    });
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="rep-rate-table">
            {/* Header */}
            <div className="rep-rate-table-header" style={{ display: 'flex', gap: '10px' }}>
              <div className="rep-rate-col rep-rate-col-category" style={{ flex: '2' }}>Brand</div>
              <div className="rep-rate-col" style={{ flex: '2' }}>Pieces Title</div>
              <div className="rep-rate-col" style={{ flex: '1' }}>Items</div>
              <div className="rep-rate-col" style={{ flex: '1' }}>Per Piece Rate ($)</div>
              <div className="rep-rate-col" style={{ flex: '2' }}>Postage Title</div>
              <div className="rep-rate-col" style={{ flex: '1' }}>Postage Used ($)</div>
            </div>

            {/* Rows */}
            {postageRowsState.length > 0 ? (
              postageRowsState.map((row, index) => {
                const brand = row?.brandName || "";
                const piecesTitle =
                  fulfillmentConfig?.pieceTitleByBrand?.[brand] ||
                  fulfillmentConfig?.title ||
                  DEFAULT_PIECES_TITLE;
                const postageTitle =
                  fulfillmentConfig?.postageTitleByBrand?.[brand] ||
                  fulfillmentConfig?.postageTitle ||
                  DEFAULT_POSTAGE_TITLE;
                const brandRate =
                  fulfillmentConfig?.perPieceRateByBrand?.[brand] ??
                  fulfillmentConfig?.perPieceRate ??
                  "";
                const piecesTitleError = getBrandMapError("pieceTitleByBrand", brand);
                const perPieceRateError = getBrandMapError("perPieceRateByBrand", brand);
                const postageTitleError = getBrandMapError("postageTitleByBrand", brand);

                return (
                  <div
                    className="rep-rate-table-row mt-2"
                    style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}
                    key={row?.rowKey || `brand-row-${index}`}
                  >
                    {/* Brand */}
                    <div className="rep-rate-col rep-rate-col-category" style={{ flex: '2' }}>
                      <div className="rep-rate-category-cell">
                        <Form.Control
                          type="text"
                          value={brand || row?.brandId || "-"}
                          readOnly
                          className="input-field rep-rate-title-input"
                        />
                      </div>
                    </div>

                    {/* Pieces Title */}
                    <div className="rep-rate-col" style={{ flex: '2' }}>
                      {Boolean(fulfillmentConfig?.chargeBrandwise) ? (
                        <>
                          <Form.Control
                            type="text"
                            value={piecesTitle}
                            onChange={(event) => {
                              const nextValue = event?.target?.value;
                              setFulfillmentConfig((prev) => ({
                                ...prev,
                                pieceTitleByBrand: {
                                  ...(prev?.pieceTitleByBrand || {}),
                                  [brand]: nextValue,
                                },
                              }));
                            }}
                            isInvalid={Boolean(piecesTitleError)}
                            className="input-field"
                          />
                          <Form.Control.Feedback type="invalid">
                            {piecesTitleError}
                          </Form.Control.Feedback>
                        </>
                      ) : (
                        <div className="rep-rate-total-display p-2 bg-light rounded text-center border">
                          {piecesTitle}
                        </div>
                      )}
                    </div>

                    {/* Items */}
                    <div className="rep-rate-col" style={{ flex: '1' }}>
                      <div className="rep-rate-total-display p-2 bg-light rounded text-center border">
                        {Number(parseNumber(row?.items).toFixed(2)).toLocaleString()}
                      </div>
                    </div>

                    {/* Per Piece Rate */}
                    <div className="rep-rate-col" style={{ flex: '1' }}>
                      {Boolean(fulfillmentConfig?.chargeBrandwise) ? (
                        <>
                          <CurrencyInput
                            value={brandRate}
                            onChange={(event) => {
                              const nextValue = event?.target?.value;
                              setFulfillmentConfig((prev) => ({
                                ...prev,
                                perPieceRateByBrand: {
                                  ...(prev?.perPieceRateByBrand || {}),
                                  [brand]: nextValue,
                                },
                              }));
                            }}
                            isInvalid={Boolean(perPieceRateError)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {perPieceRateError}
                          </Form.Control.Feedback>
                        </>
                      ) : (
                        <div className="rep-rate-total-display p-2 bg-light rounded text-center border">
                          {formatCurrencyWithZero(fulfillmentConfig?.perPieceRate || 0)}
                        </div>
                      )}
                    </div>

                    {/* Postage Title */}
                    <div className="rep-rate-col" style={{ flex: '2' }}>
                      {Boolean(fulfillmentConfig?.chargeBrandwise) ? (
                        <>
                          <Form.Control
                            type="text"
                            value={postageTitle}
                            onChange={(event) => {
                              const nextValue = event?.target?.value;
                              setFulfillmentConfig((prev) => ({
                                ...prev,
                                postageTitleByBrand: {
                                  ...(prev?.postageTitleByBrand || {}),
                                  [brand]: nextValue,
                                },
                              }));
                            }}
                            isInvalid={Boolean(postageTitleError)}
                            className="input-field"
                          />
                          <Form.Control.Feedback type="invalid">
                            {postageTitleError}
                          </Form.Control.Feedback>
                        </>
                      ) : (
                        <div className="rep-rate-total-display p-2 bg-light rounded text-center border">
                          {postageTitle}
                        </div>
                      )}
                    </div>

                    {/* Postage Used */}
                    <div className="rep-rate-col" style={{ flex: '1' }}>
                      <div
                        className="rep-rate-total-display p-2 rounded text-center border"
                        style={{ backgroundColor: '#fff3cd' }}
                      >
                        {formatCurrencyWithZero(row?.postageUsed || 0)}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="mt-2 text-center text-muted py-3">
                No postage rows available.
              </div>
            )}

            {/* Footer totals */}
            <div
              className="rep-rate-table-row mt-2 border-top pt-2"
              style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontWeight: 600 }}
            >
                <div style={{ flex: '2' }}>
                  {Boolean(fulfillmentConfig?.chargeBrandwise) ? "Program Total" : "Total"}
                </div>
                <div style={{ flex: '2' }}>
                  {Boolean(fulfillmentConfig?.chargeBrandwise) ? (
                    <div className="rep-rate-total-display p-2 bg-light rounded text-center border">
                      {fulfillmentConfig.title || DEFAULT_PIECES_TITLE}
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        value={fulfillmentConfig.title || ""}
                        onChange={handleChange(setFulfillmentConfig, "title")}
                        isInvalid={Boolean(getError("fulfillment", "title"))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError("fulfillment", "title")}
                      </Form.Control.Feedback>
                    </>
                  )}
                </div>
                <div
                  className="rep-rate-total-display p-2 bg-light rounded text-center border"
                  style={{ flex: '1' }}
                >
                  {Number(
                    postageRowsState.reduce(
                      (sum, value) => sum + parseNumber(value?.items),
                      0
                    ).toFixed(2)
                  ).toLocaleString()}
                </div>
                <div style={{ flex: '1' }}>
                  {Boolean(fulfillmentConfig?.chargeBrandwise) ? (
                    <div className="rep-rate-total-display p-2 bg-light rounded text-center border">
                      {formatCurrencyWithZero(fulfillmentConfig.perPieceRate || 0)}
                    </div>
                  ) : (
                    <>
                      <CurrencyInput
                        value={fulfillmentConfig.perPieceRate || ""}
                        onChange={handleChange(setFulfillmentConfig, "perPieceRate")}
                        isInvalid={Boolean(getError("fulfillment", "perPieceRate"))}
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError("fulfillment", "perPieceRate")}
                      </Form.Control.Feedback>
                    </>
                  )}
                </div>
                <div style={{ flex: '2' }}>
                  {Boolean(fulfillmentConfig?.chargeBrandwise) ? (
                    <div className="rep-rate-total-display p-2 bg-light rounded text-center border">
                      {fulfillmentConfig.postageTitle || DEFAULT_POSTAGE_TITLE}
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        value={fulfillmentConfig.postageTitle || ""}
                        onChange={handleChange(setFulfillmentConfig, "postageTitle")}
                        isInvalid={Boolean(getError("fulfillment", "postageTitle"))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError("fulfillment", "postageTitle")}
                      </Form.Control.Feedback>
                    </>
                  )}
                </div>
                <div
                  className="rep-rate-total-display p-2 rounded text-center border"
                  style={{ flex: '1', backgroundColor: '#fff3cd' }}
                >
                  {formatCurrencyWithZero(
                    postageRowsState.reduce(
                      (sum, value) => sum + parseNumber(value?.postageUsed),
                      0
                    )
                  )}
                </div>
              </div>
          </div>
        </div>
      )}

      {shouldShow("fulfillment_misc") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Fulfilment Misc</span>
          </h3>
          {fulfillmentMiscRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleFulfillmentMiscFieldChange(row.id, "title")}
                      isInvalid={Boolean(getError("fulfillment_misc", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fulfillment_misc", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Rate ($)</Form.Label>
                    <CurrencyInput
                      value={row.rate}
                      onChange={handleFulfillmentMiscFieldChange(row.id, "rate")}
                      isInvalid={Boolean(getError("fulfillment_misc", "rate", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fulfillment_misc", "rate", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Items</Form.Label>
                    <CurrencyInput
                      value={row.items}
                      onChange={handleFulfillmentMiscFieldChange(row.id, "items")}
                      isInvalid={Boolean(getError("fulfillment_misc", "items", rowIndex))}
                      decimalScale={0}
                      prefix=""
                      placeholder="0"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fulfillment_misc", "items", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("fulfillment_misc", row.id)}
                    aria-label="Delete fulfilment misc row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddFulfillmentMiscRow}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("fulfillment_tracker") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2 mb-3">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">
              Refund checks
            </span>
          </h3>

          <Row className="mb-3">
            <Col sm={3}>
              <Form.Group className="mb-0">
                <Form.Label className="label-input">Check Processing Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.checkProcessingTitle || ""}
                  onChange={handleChange(setTrackerConfig, "checkProcessingTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "checkProcessingTitle"))}
                  className="input-field"
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group className="mb-0">
                <Form.Label className="label-input">Refund Checks Issued Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.refundChecksIssuedTitle || ""}
                  onChange={handleChange(setTrackerConfig, "refundChecksIssuedTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "refundChecksIssuedTitle"))}
                  className="input-field"
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group className="mb-0">
                <Form.Label className="label-input">Void Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.voidProcessingTitle || ""}
                  onChange={handleChange(setTrackerConfig, "voidProcessingTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "voidProcessingTitle"))}
                  className="input-field"
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group className="mb-0">
                <Form.Label className="label-input">Stopped Payment Title</Form.Label>
                <Form.Control
                  type="text"
                  value={trackerConfig.stoppedPaymentTitle || ""}
                  onChange={handleChange(setTrackerConfig, "stoppedPaymentTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "stoppedPaymentTitle"))}
                  className="input-field"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <Form.Group className="mb-0">
                <Form.Label className="label-input">Check Processing Rate ($)</Form.Label>
                <CurrencyInput
                  value={trackerConfig.checkProcessingRate || ""}
                  onChange={handleChange(setTrackerConfig, "checkProcessingRate")}
                  isInvalid={Boolean(getError("fulfillment_tracker", "checkProcessingRate"))}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Table matching screenshot layout */}
          <div className="tracker-table">
            {/* Header row – static column headers matching screenshot */}
            <div className="tracker-table-header">
              <div className="tracker-col tracker-col-month">Month/Year</div>
              <div className="tracker-col tracker-col-data">#of Checks</div>
              <div className="tracker-col tracker-col-value">Check Amount ($)</div>
              <div className="tracker-col tracker-col-data">#of Voided</div>
              <div className="tracker-col tracker-col-value">Void Amount ($)</div>
              <div className="tracker-col tracker-col-stop">Stopped Payment Amount ($)</div>
            </div>

            {/* 12 month rows */}
            {monthRows.map((row) => {
              const monthValues = trackerConfig?.monthly?.[row.key] || {};
              const isEditable = row.key === currentMonthKey;

              return (
                <div className="tracker-table-row" key={row.key}>
                  <div className="tracker-col tracker-col-month">{row.label}</div>
                  <div className="tracker-col tracker-col-data">
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={monthValues.checkCount || ""}
                      onChange={handleMonthlyFieldChange(
                        setTrackerConfig,
                        row.key,
                        "checkCount",
                        { wholeNonNegative: true }
                      )}
                      disabled={!isEditable}
                      className="input-field"
                      style={{ backgroundColor: isEditable ? '#fff' : '#e9ecef' }}
                    />
                  </div>
                  <div className="tracker-col tracker-col-value">
                    <CurrencyInput
                      value={monthValues.checkAmount || ""}
                      onChange={handleMonthlyFieldChange(
                        setTrackerConfig,
                        row.key,
                        "checkAmount"
                      )}
                      disabled={!isEditable}
                      style={{ backgroundColor: isEditable ? '#fff' : '#e9ecef' }}
                    />
                  </div>
                  <div className="tracker-col tracker-col-data">
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={monthValues.voidCount || ""}
                      onChange={handleMonthlyFieldChange(
                        setTrackerConfig,
                        row.key,
                        "voidCount",
                        { wholeNonNegative: true }
                      )}
                      disabled={!isEditable}
                      className="input-field"
                      style={{ backgroundColor: isEditable ? '#fff' : '#e9ecef' }}
                    />
                  </div>
                  <div className="tracker-col tracker-col-value">
                    <CurrencyInput
                      value={monthValues.voidAmount || ""}
                      onChange={handleMonthlyFieldChange(
                        setTrackerConfig,
                        row.key,
                        "voidAmount",
                        { alwaysNegative: true }
                      )}
                      allowNegative
                      disabled={!isEditable}
                      style={{ backgroundColor: isEditable ? '#fff' : '#e9ecef' }}
                    />
                  </div>
                  <div className="tracker-col tracker-col-stop">
                    <CurrencyInput
                      value={monthValues.stoppedPaymentAmount || ""}
                      onChange={handleMonthlyFieldChange(
                        setTrackerConfig,
                        row.key,
                        "stoppedPaymentAmount"
                      )}
                      disabled={!isEditable}
                      style={{ backgroundColor: isEditable ? '#fff' : '#e9ecef' }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Summary Total footer */}
            <div className="tracker-table-summary">
              <div className="tracker-col tracker-col-month tracker-summary-label">Summary Total</div>
              <div className="tracker-col tracker-col-data tracker-summary-value">
                {trackerTotals.checks || "-"}
              </div>
              <div className="tracker-col tracker-col-value tracker-summary-value">
                {formatCurrency(trackerTotals.checkAmount)}
              </div>
              <div className="tracker-col tracker-col-data tracker-summary-value">
                {trackerTotals.voided || "-"}
              </div>
              <div className="tracker-col tracker-col-value tracker-summary-value">
                {formatCurrency(trackerTotals.voidAmount)}
              </div>
              <div className="tracker-col tracker-col-stop tracker-summary-value">
                {formatCurrency(trackerTotals.stoppedAmount)}
              </div>
            </div>
          </div>

          {/* Stat cards */}
          <div className="tracker-stats-row">
            <div className="tracker-stat-card">
              <div className="tracker-stat-title">Current Month Check Amount</div>
              <div className="tracker-stat-value">
                <span className="tracker-stat-number">
                  {formatCurrency(currentTrackerMonthValues.checkAmount)}
                </span>
                <span className="tracker-stat-label">
                  {currentTrackerMonthValues.checkCount || 0} Checks
                </span>
              </div>
            </div>
            <div className="tracker-stat-card">
              <div className="tracker-stat-title">Current Month Voided Amount</div>
              <div className="tracker-stat-value">
                <span className="tracker-stat-number">
                  {formatCurrency(currentTrackerMonthValues.voidAmount)}
                </span>
                <span className="tracker-stat-label">
                  {currentTrackerMonthValues.voidCount || 0} Voided
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {shouldShow("fulfillment_tracker_1") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2 mb-3">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">
              RK/non-RK tracker
            </span>
          </h3>

          {/* Table */}
          <div className="tracker-table">
            {/* Header – editable title inputs as column headers */}
            <div className="tracker-table-header">
              <div className="tracker1-col tracker1-col-month">Month/Year</div>
              <div className="tracker1-col tracker1-col-data">
                <Form.Control
                  type="text"
                  value={tracker1Config.retrievalTitle || ""}
                  onChange={handleChange(setTracker1Config, "retrievalTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "retrievalTitle"))}
                  className="input-field tracker-header-input"
                />
              </div>
              <div className="tracker1-col tracker1-col-data">
                <Form.Control
                  type="text"
                  value={tracker1Config.nonRetrievalTitle || ""}
                  onChange={handleChange(setTracker1Config, "nonRetrievalTitle")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "nonRetrievalTitle"))}
                  className="input-field tracker-header-input"
                />
              </div>
            </div>

            {/* 12 month rows */}
            {monthRows.map((row) => {
              const monthValues = tracker1Config?.monthly?.[row.key] || {};
              const isEditable = row.key === currentMonthKey;

              return (
                <div className="tracker-table-row" key={row.key}>
                  <div className="tracker1-col tracker1-col-month">{row.label}</div>
                  <div className="tracker1-col tracker1-col-data">
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={monthValues.retrievalCount || ""}
                      onChange={handleMonthlyFieldChange(
                        setTracker1Config,
                        row.key,
                        "retrievalCount",
                        { wholeNonNegative: true }
                      )}
                      disabled={!isEditable}
                      className="input-field"
                      style={{ backgroundColor: isEditable ? '#fff' : '#e9ecef' }}
                    />
                  </div>
                  <div className="tracker1-col tracker1-col-data">
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={monthValues.nonRetrievalCount || ""}
                      onChange={handleMonthlyFieldChange(
                        setTracker1Config,
                        row.key,
                        "nonRetrievalCount",
                        { wholeNonNegative: true }
                      )}
                      disabled={!isEditable}
                      className="input-field"
                      style={{ backgroundColor: isEditable ? '#fff' : '#e9ecef' }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Summary Total footer */}
            <div className="tracker-table-summary">
              <div className="tracker1-col tracker1-col-month tracker-summary-label">Summary Total</div>
              <div className="tracker1-col tracker1-col-data tracker-summary-value">
                {tracker1Totals.retrievalCount || "-"}
              </div>
              <div className="tracker1-col tracker1-col-data tracker-summary-value">
                {tracker1Totals.nonRetrievalCount || "-"}
              </div>
            </div>
          </div>

          {/* Bottom row: stat card + conversion note (only when billing by hours) */}
          {isBillRetrievalByHours && (
            <div className="tracker1-bottom-row">
              <div className="tracker1-stat-card">
                <div className="tracker1-stat-title">Retrieval Hours</div>
                <div className="tracker1-stat-value">
                  <span className="tracker1-stat-number">
                    {monthlyHours > 0
                      ? Number(monthlyHours.toFixed(2))
                      : "—"}
                  </span>
                  <span className="tracker1-stat-label">HRS</span>
                </div>
              </div>
              <div className="tracker1-conversion-note">
                <div className="tracker1-conversion-title">Conversion Note</div>
                <div className="tracker1-conversion-body">
                  <strong>
                    ({currentTracker1MonthValues.retrievalCount || 0} × {tracker1Config.minutesPerRetrieval || 0}) / 60 = {Number(monthlyHours.toFixed(2))} Hours
                  </strong>
                </div>
                <div className="tracker1-conversion-sub">
                  Used directly for invoice calculation.
                </div>
              </div>
            </div>
          )}

          {/* Extra fields below */}
          <Row className="mt-3">
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Retrieval Rate ($)</Form.Label>
                <CurrencyInput
                  value={tracker1Config.retrievalRate}
                  onChange={handleChange(setTracker1Config, "retrievalRate")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "retrievalRate"))}
                />
                <Form.Control.Feedback type="invalid">
                  {getError("fulfillment_tracker_1", "retrievalRate")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Non Retrieval Rate ($)</Form.Label>
                <CurrencyInput
                  value={tracker1Config.nonRetrievalRate}
                  onChange={handleChange(setTracker1Config, "nonRetrievalRate")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "nonRetrievalRate"))}
                />
                <Form.Control.Feedback type="invalid">
                  {getError("fulfillment_tracker_1", "nonRetrievalRate")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3 mt-4">
                <Form.Check
                  id="bill-retrieval-by-hours"
                  type="switch"
                  label="Bill Retrieval by Hours"
                  checked={Boolean(tracker1Config.billRetrievalByHours)}
                  onChange={(event) =>
                    setTracker1Config((prev) => ({
                      ...prev,
                      billRetrievalByHours: Boolean(event?.target?.checked),
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          {isBillRetrievalByHours && (
            <Row>
              <Col sm={6}>
                <Form.Group className="mb-3">
                <Form.Label className="label-input">
                  Minutes Per Retrieval
                </Form.Label>
                <Form.Control
                  type="text"
                  value={tracker1Config.minutesPerRetrieval}
                  onChange={handleChange(setTracker1Config, "minutesPerRetrieval")}
                  isInvalid={Boolean(getError("fulfillment_tracker_1", "minutesPerRetrieval"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("fulfillment_tracker_1", "minutesPerRetrieval")}</Form.Control.Feedback>
              </Form.Group>
              </Col>
            </Row>
          )}
        </div>
      )}

      {shouldShow("supplies") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Supplies</span>
          </h3>
          {suppliesRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setSuppliesRows, row.id, "title")}
                      isInvalid={Boolean(getError("supplies", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("supplies", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Charge</Form.Label>
                    <CurrencyInput
                      value={row.charge}
                      onChange={handleRowFieldChange(setSuppliesRows, row.id, "charge")}
                      isInvalid={Boolean(getError("supplies", "charge", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("supplies", "charge", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("supplies", row.id)}
                    aria-label="Delete supplies row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddRow(setSuppliesRows, nextSuppliesIdRef, createTitleChargeRow, "Supplies")}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("storage") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Storage</span>
          </h3>
          {storageRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setStorageRows, row.id, "title")}
                      isInvalid={Boolean(getError("storage", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("storage", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Charge</Form.Label>
                    <CurrencyInput
                      value={row.charge}
                      onChange={handleRowFieldChange(setStorageRows, row.id, "charge")}
                      isInvalid={Boolean(getError("storage", "charge", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("storage", "charge", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("storage", row.id)}
                    aria-label="Delete storage row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddRow(setStorageRows, nextStorageIdRef, createTitleChargeRow, "Storage")}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("gift_cards") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Gift Cards</span>
          </h3>
          {giftCardRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setGiftCardRows, row.id, "title")}
                      isInvalid={Boolean(getError("gift_cards", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("gift_cards", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Processing fee</Form.Label>
                    <CurrencyInput
                      value={row.processingFee}
                      onChange={handleRowFieldChange(setGiftCardRows, row.id, "processingFee")}
                      isInvalid={Boolean(getError("gift_cards", "processingFee", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("gift_cards", "processingFee", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input"># of processed</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={row.numberProcessed}
                      onChange={handleRowFieldChange(setGiftCardRows, row.id, "numberProcessed", {
                        wholeNonNegative: true,
                      })}
                      isInvalid={Boolean(getError("gift_cards", "numberProcessed", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("gift_cards", "numberProcessed", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("gift_cards", row.id)}
                    aria-label="Delete gift cards row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddRow(setGiftCardRows, nextGiftCardIdRef, createGiftCardInvoiceRow)}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {shouldShow("fedex_expenses") && (
        <div className="invoice-section-div">
          <h3 className="d-flex align-items-center gap-2">
            <img src="/assets/images/invoice-user-icon.svg" alt="" />
            <span className="font-16 font-weight-bold heading-color">Shipment Charges</span>
          </h3>
          {fedexRows.map((row, rowIndex) => (
            <div className="bg-light p-3 border-sm mb-3" key={row.id}>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={row.title}
                      onChange={handleRowFieldChange(setFedexRows, row.id, "title")}
                      isInvalid={Boolean(getError("fedex_expenses", "title", rowIndex))}
                      className="input-field"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fedex_expenses", "title", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="label-input">Base charge</Form.Label>
                    <CurrencyInput
                      value={row.baseCharge}
                      onChange={handleRowFieldChange(setFedexRows, row.id, "baseCharge")}
                      isInvalid={Boolean(getError("fedex_expenses", "baseCharge", rowIndex))}
                      decimalScale={2}
                      placeholder="$ 0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError("fedex_expenses", "baseCharge", rowIndex)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={1} className="text-center">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0"
                    onClick={() => handleDeleteRowClick("fedex_expenses", row.id)}
                    aria-label="Delete shipment charges row"
                  >
                    <img src="/assets/images/delete-icon.svg" alt="" />
                  </button>
                </Col>
              </Row>
            </div>
          ))}
          <button
            type="button"
            className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
            onClick={handleAddRow(setFedexRows, nextFedexIdRef, createFedexInvoiceRow)}
          >
            <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
          </button>
        </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={handleCloseDeleteModal}
          onDelete={handleConfirmDeleteRow}
          message="Are you sure you want to delete this row?"
        />
      )}
    </>
  );
};

export default FulfilmentTab;